import { useMemo } from 'react'
import { ArrowLeftIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { ScoopButton, ScoopRow, ScoopText } from '../../../../ui'
import { ReportHeader } from '../InvestigationReport/components/ReportHeader/ReportHeader'
import { ReportBreadcrumb } from '../ReportBreadcrumb/ReportBreadcrumb'
import { ReportState } from '../ReportState/ReportState'
import { SeverityTag } from '../SeverityTag/SeverityTag'
import { EntityDetail } from '../InvestigationReport/components/EntityDetail/EntityDetail'
import {
    actionMatchesEntity,
    getEntityDisplayId,
    getEntityHierarchyPathLabel,
    getEntityId,
    getEntityTypeLabel,
} from '../../utils/hierarchyUtils'
import { isKnownSeverity } from '../../utils/severity'
import styles from './EntityReportPage.module.scss'

const findEntity = (entities, entityId) =>
    entities.find((entity) => {
        const id = getEntityId(entity)
        return id === entityId || getEntityDisplayId(entity) === entityId
    })

export const EntityReportPage = ({
    report,
    loadingReport,
    reportError,
    selectedRunPath,
    selectedEntity,
    actionCandidates = [],
    onBack,
    onAcceptAction,
    onRejectAction,
    acceptingAction,
    rejectingAction,
    pendingFingerprint,
    breadcrumbs,
}) => {
    const entityId = selectedEntity?.entityId
    const reportEntities = report?.entitySummaries || []
    const entity = useMemo(
        () => findEntity(reportEntities, entityId) || selectedEntity?.entity || selectedEntity,
        [entityId, reportEntities, selectedEntity]
    )

    const entityTypeLabel = getEntityTypeLabel(report, entity)
    const entityDisplayId = getEntityDisplayId(entity)
    const hierarchyLevels = report?.hierarchyLevels || selectedEntity?.hierarchyLevels || []
    const hierarchyPath = getEntityHierarchyPathLabel(entity, hierarchyLevels)

    const probes = useMemo(
        () =>
            (report?.probeIndex || []).filter((probe) => {
                const probeEntityId = probe.entityId || probe.scopeEntityId
                return probeEntityId === getEntityId(entity)
            }),
        [entity, report?.probeIndex]
    )

    const scopedActions = useMemo(
        () => actionCandidates.filter((candidate) => actionMatchesEntity(candidate, entity)),
        [actionCandidates, entity]
    )

    if (loadingReport && !entity) {
        return <ReportState title="Loading entity report…" />
    }

    if (reportError) {
        return (
            <ReportState
                variant="error"
                title="Error loading entity report"
                message={reportError.message}
            />
        )
    }

    if (!entity || !getEntityId(entity)) {
        return (
            <ReportState
                title="Entity not found"
                message="This entity is not present in the selected report."
                action={
                    <ScoopButton variant="grey" size="sm" onClick={onBack}>
                        Back
                    </ScoopButton>
                }
            />
        )
    }

    return (
        <div className={styles.container}>
            {breadcrumbs?.length > 1 && (
                <div className={styles.breadcrumbRow}>
                    <ReportBreadcrumb crumbs={breadcrumbs} />
                </div>
            )}
            <div className={styles.header}>
                <ScoopButton variant="grey" size="sm" onClick={onBack}>
                    <ArrowLeftIcon size={14} />
                    Back
                </ScoopButton>
            </div>

            <div className={styles.body}>
                <ReportHeader
                    title={entityDisplayId || `${entityTypeLabel} detail`}
                    entityType={entityTypeLabel}
                    status={report?.status}
                    timestamp={report?.timestamp}
                    titleAccessory={
                        isKnownSeverity(entity.severity) ? (
                            <SeverityTag severity={entity.severity} />
                        ) : undefined
                    }
                />

                {/* One metadata line (review D25): path · counts — text, not a
                    row of chips. The entity name lives only in the masthead. */}
                {(hierarchyPath ||
                    scopedActions.length > 0 ||
                    probes.length > 0 ||
                    entity.unprobed === true) && (
                    <ScoopRow className={styles.contextBar}>
                        <ScoopText className={styles.contextText}>
                            {[
                                hierarchyPath,
                                entity.investigationCount != null
                                    ? `${entity.investigationCount} analyses`
                                    : null,
                                probes.length > 0 ? `${probes.length} probes` : null,
                                scopedActions.length > 0
                                    ? `${scopedActions.length} actions`
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </ScoopText>
                        {entity.unprobed === true && (
                            <span className={styles.notProbed}>
                                <WarningCircleIcon size={14} className={styles.notProbedIcon} />
                                Not probed
                            </span>
                        )}
                    </ScoopRow>
                )}

                <EntityDetail
                    entity={entity}
                    probes={probes}
                    runPath={selectedRunPath}
                    actionCandidates={scopedActions}
                    onAcceptAction={onAcceptAction}
                    onRejectAction={onRejectAction}
                    acceptingAction={acceptingAction}
                    rejectingAction={rejectingAction}
                    pendingFingerprint={pendingFingerprint}
                    variant="section"
                />
            </div>
        </div>
    )
}
